/**
 * brand.js — build-time brand identity (name, logo, favicon, colour).
 *
 * Enterprise on-prem installs set REACT_APP_BRAND_* at build time (deploy/.env →
 * Docker build args → CRA bakes them in). These become the DEFAULT brand
 * everywhere, so a customer build never flashes our "LMS Campus" / "Visible
 * Campus" name or logo before the per-institute branding API responds.
 *
 * SaaS is unaffected: when the REACT_APP_BRAND_* vars are unset the resolvers
 * return the caller's existing SaaS value (hostname-based logo / name), so
 * behaviour is byte-for-byte identical to before.
 */
import { isEnterprise } from './edition';

const val = (k) => {
  const v = process.env[k];
  return v && String(v).trim() ? String(v).trim() : null;
};

export const BRAND_NAME = val('REACT_APP_BRAND_NAME');
export const BRAND_SUBTITLE = val('REACT_APP_BRAND_SUBTITLE');
export const BRAND_LOGO_URL = val('REACT_APP_BRAND_LOGO_URL');
export const BRAND_FAVICON_URL = val('REACT_APP_BRAND_FAVICON_URL') || BRAND_LOGO_URL;
export const BRAND_PRIMARY_COLOR = val('REACT_APP_BRAND_PRIMARY_COLOR');

// Neutral placeholder mark (a generic graduation cap) — used ONLY in an
// enterprise build that didn't set a logo. It must never be one of our SaaS
// logos. Self-contained data URI so it works in <img src> with no extra asset.
const GENERIC_LOGO_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">' +
  '<rect width="64" height="64" rx="14" fill="#3b82f6"/>' +
  '<path d="M32 17 8 27l24 10 16-6.7V40h4V27L32 17Zm-13 17.5V42c0 3.3 5.8 6 13 6s13-2.7 13-6v-7.5L32 41l-13-6.5Z" fill="#fff"/>' +
  '</svg>';
export const GENERIC_LOGO = `data:image/svg+xml,${encodeURIComponent(GENERIC_LOGO_SVG)}`;

// Neutral name fallback for an enterprise build that didn't set REACT_APP_BRAND_NAME.
// Deliberately NOT one of our SaaS brands.
const ENTERPRISE_FALLBACK_NAME = 'Learning Portal';

/** Brand name for this build: operator value → enterprise neutral → the SaaS name. */
export const resolveBrandName = (saasName) =>
  BRAND_NAME || (isEnterprise ? ENTERPRISE_FALLBACK_NAME : saasName);

/** Logo for this build: operator value → enterprise neutral mark → the SaaS logo. */
export const resolveBrandLogo = (saasLogo) =>
  BRAND_LOGO_URL || (isEnterprise ? GENERIC_LOGO : saasLogo);

/** Favicon for this build: operator value → enterprise neutral mark → the SaaS favicon. */
export const resolveBrandFavicon = (saasFavicon) =>
  BRAND_FAVICON_URL || (isEnterprise ? GENERIC_LOGO : saasFavicon);

/** Subtitle for this build: operator value → the SaaS subtitle. */
export const resolveBrandSubtitle = (saasSubtitle) =>
  BRAND_SUBTITLE || saasSubtitle;
